import React, { useEffect } from 'react'
import type { NodeProps } from '@xyflow/react'
import { FrameConnection } from '@/components/canvas/nodes/variants/FrameConnection'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import { Icon } from '@/components/ui/icon'
import { useStudio, useStudioAsset } from '@/components/studio/StudioContext'

/**
 * Frame Input — an image-first node that shows the Artboard frame version it is
 * pinned to. Choosing the frame and the version happens in the node settings
 * panel; the node face stays a picture, like an image file node.
 */
function FrameInputComponent({ id, data, selected }: NodeProps) {
  const studio = useStudio()
  const state = (data.state ?? {}) as Record<string, unknown>
  const update = useNodeStateChange(id)
  const frame = studio.project?.artboards.find(f => f.id === state.frameId)
  const version = frame?.versions.find(v => v.id === state.versionId)
  const versionNumber = frame && version ? frame.versions.findIndex(v => v.id === version.id) + 1 : 0
  const { url, error } = useStudioAsset(version?.assetId)
  useEffect(() => { update({ images: url ? [url] : [], frameInput: frame && version ? { frameId: frame.id, versionId: version.id } : null }) }, [url, frame?.id, version?.id, update])
  const stale = Boolean(frame && version && frame.versions[frame.activeVersion]?.id !== version.id)

  return <>
    <NodeHandles nodeId={id} handles={STANDARD_HANDLES.FrameInput} />
    <FrameConnection id={id} title={frame?.title || 'Frame Input'} selected={selected} state={state} onStateChange={update}>
      {url ? (
        <div className="relative w-full">
          <img alt={frame?.title || 'Source frame'} src={url} className="aspect-square w-full rounded-[10px] bg-muted object-contain" />
          <div className="pointer-events-none absolute inset-x-2 bottom-2 flex items-center gap-1.5">
            <span className="truncate rounded-md bg-black/65 px-2 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
              {frame?.title}{versionNumber ? ` · v${versionNumber}` : ''}
            </span>
            {stale && (
              <span
                role="status"
                title="Artboard has a newer version. This input stays pinned."
                className="flex shrink-0 items-center gap-1 rounded-md bg-amber-500/90 px-1.5 py-1 text-[11px] font-medium text-amber-950"
              >
                <Icon name="info" className="h-3 w-3" />
                pinned
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="flex aspect-square w-full flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed border-border px-4 text-center">
          <Icon name="frame" className="h-6 w-6 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">No frame selected</span>
          <span className="text-xs text-muted-foreground">Pick an Artboard frame in Node controls</span>
        </div>
      )}
      {error && <p role="alert" className="px-2 pt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </FrameConnection>
  </>
}
export const FrameInputNode = React.memo(FrameInputComponent, (a, b) => a.id === b.id && a.selected === b.selected && a.data.state === b.data.state)
